import { create } from 'zustand';
import { hasPermission, type OrgKind, type Permission, type Portal } from '@kts/shared';
import type { Api } from './api';

/** `/me` javobi. */
export interface Me {
  user: {
    id: string;
    fullName: string;
    phone: string | null;
    login: string | null;
    email: string | null;
    avatarUrl: string | null;
    locale: string;
    mustChangePassword: boolean;
  };
  org: { id: string; kind: OrgKind; code: string; name: string; logoUrl: string | null } | null;
  portal: Portal;
  isSuper: boolean;
  perms: string[];
  roles: { code: string; name: string; nameRu: string | null }[];
  memberships: { orgId: string; orgKind: OrgKind; portal: Portal; orgName: string; orgCode: string; title: string | null; isOwner: boolean }[];
  prefs: { theme: string; palette: string; data: Record<string, unknown> };
}

type Status = 'loading' | 'anon' | 'authed';

interface AuthState {
  status: Status;
  me: Me | null;
  perms: Set<string>;
  init: (api: Api) => Promise<void>;
  login: (api: Api, login: string, password: string) => Promise<Me>;
  logout: (api: Api) => Promise<void>;
  switchOrg: (api: Api, orgId: string) => Promise<Me>;
  reloadMe: (api: Api) => Promise<Me | null>;
  lost: () => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading',
  me: null,
  perms: new Set(),

  async init(api) {
    const ok = await api.restore();
    if (!ok) {
      set({ status: 'anon', me: null, perms: new Set() });
      return;
    }
    await get().reloadMe(api);
  },

  async reloadMe(api) {
    try {
      const me = await api.get<Me>('/me');
      set({ status: 'authed', me, perms: new Set(me.perms) });
      return me;
    } catch {
      set({ status: 'anon', me: null, perms: new Set() });
      return null;
    }
  },

  async login(api, login, password) {
    await api.login(login, password);
    const me = await get().reloadMe(api);
    if (!me) throw new Error('Profil yuklanmadi');
    return me;
  },

  async logout(api) {
    await api.logout();
    set({ status: 'anon', me: null, perms: new Set() });
  },

  async switchOrg(api, orgId) {
    await api.switchOrg(orgId);
    const me = await get().reloadMe(api);
    if (!me) throw new Error('Profil yuklanmadi');
    return me;
  },

  lost() {
    set({ status: 'anon', me: null, perms: new Set() });
  },
}));

/** Ruxsat tekshiruvi (UI elementlarini yashirish uchun; haqiqiy tekshiruv serverda). */
export function useCan() {
  const perms = useAuth((s) => s.perms);
  const isSuper = useAuth((s) => s.me?.isSuper ?? false);
  return (...p: Permission[]) => p.some((x) => hasPermission(perms, x, isSuper));
}
